/**
 * Helper functions for aggregating calculated periods
 * These functions extract common aggregation patterns
 */

import { sumBy, uniq } from 'lodash-es';
import { date } from '~/lib/date';
import { calculateWeightedRatesByEnergyFromSegments } from '../weightedRateUtils';
import { CalculatedProjectOfferPeriod } from './reportTypes';

/**
 * Environmental rate types that need to be calculated
 */
const ENVIRONMENTAL_RATE_TYPES = ['lret', 'sres', 'ess', 'veet', 'aeeis', 'pdrs', 'reps'] as const;

/**
 * Calculate weighted average of environmental rates across multiple periods
 */
export function calculateWeightedEnvironmentalRates(periods: CalculatedProjectOfferPeriod[]): Record<string, number> {
  const totalDays = sumBy(periods, 'numOfDays');
  if (totalDays === 0) {
    return {};
  }

  const result: Record<string, number> = {};
  for (const rateType of ENVIRONMENTAL_RATE_TYPES) {
    result[rateType] = sumBy(periods, (p) => ((p as any)[rateType] ?? 0) * p.numOfDays) / totalDays;
  }

  return result;
}

/**
 * Calculate weighted average rate for a specific field
 * Used for peak, offPeak, shoulder rates
 * This matches the old logic: check if numerator > 0, then divide by denominator
 */
export function calculateWeightedAverageRate(
  periods: CalculatedProjectOfferPeriod[],
  rateField: keyof CalculatedProjectOfferPeriod,
  dailyEnergyField: number
): number {
  const numerator = sumBy(periods, (p) => {
    const rate = (p as any)[rateField] ?? 0;
    return rate * dailyEnergyField * p.numOfDays;
  });

  const denominator = sumBy(periods, (p) => dailyEnergyField * p.numOfDays);

  // Match old logic: check numerator > 0, then divide by denominator
  return numerator > 0 && denominator > 0 ? numerator / denominator : 0;
}

/**
 * Rate Unit (weighted avg) - uses shared weightedRateUtils (ConsumptionIncLoss when present, else dailyEnergy × numOfDays).
 * Display only.
 */
export type SegmentLike = {
  peak?: number;
  offPeak?: number;
  shoulder?: number;
  numOfDays?: number;
  startDate?: string;
  endDate?: string;
  peakConsumptionIncLoss?: number;
  offPeakConsumptionIncLoss?: number;
  shoulderConsumptionIncLoss?: number;
};

/**
 * Filter segments to the year range [periodStart + yearIndex years, periodStart + (yearIndex+1) years],
 * capped by periodEndDate. Uses actual dates only (no periodName).
 * yearIndex 0 = first year of period, 1 = second year, etc.
 */
export function filterSegmentsByPeriodYear<T extends { startDate?: string; endDate?: string }>(
  segments: T[],
  periodStartDate: string,
  periodEndDate: string,
  yearIndex: number
): T[] {
  if (!segments?.length || !periodStartDate || !periodEndDate || yearIndex < 0) return segments;
  const start = date(periodStartDate);
  const periodEndInclusive = date(periodEndDate);
  const yearStart = start.add(yearIndex, 'year');
  const yearExclusiveEnd = start.add(yearIndex + 1, 'year');
  const yStartVal = yearStart.valueOf();
  // `periodEndDate` is inclusive; +1 day matches half-open `[…)` clipping used in Current vs New.
  const periodExclusiveEnd = periodEndInclusive.add(1, 'day').valueOf();
  const yEndVal = Math.min(yearExclusiveEnd.valueOf(), periodExclusiveEnd);
  if (yStartVal >= yEndVal) return [];
  return segments.filter((seg) => {
    if (!seg.startDate || !seg.endDate) return false;
    const segStart = date(seg.startDate).valueOf();
    const segExclusiveEnd = date(seg.endDate).add(1, 'day').valueOf();
    return segStart >= yStartVal && segStart < yEndVal && segExclusiveEnd <= yEndVal;
  });
}

/**
 * Rate Unit (reverse method) - matches CurrentVsNewSection New Contract.
 * rate = total_cost / total_quantity × 100
 * Per-meter first: for each segment and meter, comp = calculateMeterConsumptionByComponent, cost += comp.peak×seg.peak/100, qty += comp.peak.
 */
export type SegmentWithMeterCosts = SegmentLike & { meterContestableCosts?: Record<string, number> };

export function calculateRateUnitReverseFromSegments(
  segments: SegmentWithMeterCosts[],
  projectMeters: Array<{
    id: string;
    annualData?: { peakEnergy?: number; offPeakEnergy?: number; shoulderEnergy?: number };
    contract?: { contractType?: string };
    dlf?: number;
    mlf?: number;
    tlf?: number;
  }>,
  calculateMeterConsumptionByComponent: (
    m: unknown,
    n: number,
    opts: { usePerMeterLossFactor: boolean }
  ) => { peak: number; offPeak: number; shoulder: number }
): { peak: number; offPeak: number; shoulder: number } {
  let peakCost = 0,
    peakQty = 0,
    offCost = 0,
    offQty = 0,
    shCost = 0,
    shQty = 0;
  const meterMap = new Map(projectMeters.map((m) => [m.id, m]));

  segments.forEach((seg) => {
    const meterIds = Object.keys(seg.meterContestableCosts ?? {});
    const meters = meterIds.map((id) => meterMap.get(id)).filter(Boolean) as typeof projectMeters;
    const n = seg.numOfDays ?? 0;
    const segPeak = seg.peak ?? 0;
    const segOffPeak = seg.offPeak ?? 0;
    const segShoulder = seg.shoulder ?? 0;

    meters.forEach((m) => {
      const comp = calculateMeterConsumptionByComponent(m, n, { usePerMeterLossFactor: true });
      peakCost += (comp.peak * segPeak) / 100;
      peakQty += comp.peak;
      offCost += (comp.offPeak * segOffPeak) / 100;
      offQty += comp.offPeak;
      shCost += (comp.shoulder * segShoulder) / 100;
      shQty += comp.shoulder;
    });
  });

  return {
    peak: peakQty > 0 ? (peakCost / peakQty) * 100 : 0,
    offPeak: offQty > 0 ? (offCost / offQty) * 100 : 0,
    shoulder: shQty > 0 ? (shCost / shQty) * 100 : 0
  };
}

/** Uses shared weightedRateUtils (display only). */
export function calculateRateUnitWeightedAvgFromSegments(
  segments: SegmentLike[],
  dailyPeak: number,
  dailyOffPeak: number,
  dailyShoulder: number
): { peak: number; offPeak: number; shoulder: number } {
  return calculateWeightedRatesByEnergyFromSegments(segments, dailyPeak, dailyOffPeak, dailyShoulder);
}

/**
 * Merge: reverse method per meter - same formula as calculateRateUnitReverseFromSegments.
 * rate = total_cost / total_quantity × 100
 * For each meter, use its recommended period's segments; sum cost and qty across all (meter, segment) pairs.
 * Uses period-year segments for rate: 12M→months 1–12, 24M→months 13–24, 36M→months 25–36.
 */
export function calculateEffectiveRateUnitWeightedAvg(
  group: Array<{
    offer: { meterIds?: string[] };
    period: {
      id?: string;
      periodName?: number;
      startDate?: string;
      environmentalRatePeriods?: SegmentLike[];
      meterContestableCosts?: Record<string, number>;
    };
  }>,
  projectMeters: Array<{
    id: string;
    annualData?: { peakEnergy?: number; offPeakEnergy?: number; shoulderEnergy?: number };
    contract?: { contractType?: string };
    dlf?: number;
    mlf?: number;
    tlf?: number;
  }>,
  calculateMeterConsumptionByComponent: (
    m: unknown,
    numOfDays: number,
    opts: { usePerMeterLossFactor: boolean }
  ) => { peak: number; offPeak: number; shoulder: number },
  meterOfferPeriodMapping?: Record<string, { recommendedPeriodId: string }>,
  mergedMeterIds?: string[]
): { peak: number; offPeak: number; shoulder: number } {
  const meterMap = new Map(projectMeters.map((m) => [m.id, m]));
  const allMeterIds =
    mergedMeterIds && mergedMeterIds.length > 0
      ? mergedMeterIds
      : uniq(
          group.flatMap(
            (g) =>
              (g.offer.meterIds ??
                (g.period.meterContestableCosts ? Object.keys(g.period.meterContestableCosts) : [])) as string[]
          )
        );

  let peakCost = 0,
    peakQty = 0,
    offCost = 0,
    offQty = 0,
    shCost = 0,
    shQty = 0;

  if (meterOfferPeriodMapping && allMeterIds.length > 0) {
    // Reverse method per meter: use all segments for weighted rate (same as eaReportCalculations)
    allMeterIds.forEach((meterId) => {
      const recId = meterOfferPeriodMapping[meterId]?.recommendedPeriodId;
      if (!recId) return;
      const period = group.find((g) => g.period.id === recId)?.period;
      const meter = meterMap.get(meterId);
      if (!period || !meter) return;
      const allSegs = period.environmentalRatePeriods || [];
      allSegs.forEach((seg) => {
        const n = seg.numOfDays ?? 0;
        const comp = calculateMeterConsumptionByComponent(meter, n, { usePerMeterLossFactor: true });
        const segPeak = seg.peak ?? 0;
        const segOff = seg.offPeak ?? 0;
        const segSh = seg.shoulder ?? 0;
        peakCost += (comp.peak * segPeak) / 100;
        peakQty += comp.peak;
        offCost += (comp.offPeak * segOff) / 100;
        offQty += comp.offPeak;
        shCost += (comp.shoulder * segSh) / 100;
        shQty += comp.shoulder;
      });
    });
  }

  if (peakQty === 0 && offQty === 0 && shQty === 0) {
    // Fallback: period-based (disjoint meter sets) - use all segments for weighted rate
    group.forEach(({ offer, period }) => {
      const meterIdsInPeriod =
        (Object.keys(period.meterContestableCosts ?? {}) as string[]).length > 0
          ? (Object.keys(period.meterContestableCosts ?? {}) as string[])
          : ((offer.meterIds ?? allMeterIds) as string[]);
      const meters = meterIdsInPeriod.map((id) => meterMap.get(id)).filter(Boolean) as typeof projectMeters;
      const allSegs = period.environmentalRatePeriods || [];
      allSegs.forEach((seg) => {
        const n = seg.numOfDays ?? 0;
        const segPeak = seg.peak ?? 0;
        const segOff = seg.offPeak ?? 0;
        const segSh = seg.shoulder ?? 0;
        meters.forEach((m) => {
          const comp = calculateMeterConsumptionByComponent(m, n, { usePerMeterLossFactor: true });
          peakCost += (comp.peak * segPeak) / 100;
          peakQty += comp.peak;
          offCost += (comp.offPeak * segOff) / 100;
          offQty += comp.offPeak;
          shCost += (comp.shoulder * segSh) / 100;
          shQty += comp.shoulder;
        });
      });
    });
  }

  return {
    peak: peakQty > 0 ? (peakCost / peakQty) * 100 : 0,
    offPeak: offQty > 0 ? (offCost / offQty) * 100 : 0,
    shoulder: shQty > 0 ? (shCost / shQty) * 100 : 0
  };
}

/**
 * Fallback when projectMeters not available. Uses shared weighted rate per period then merges by energy.
 * Display only.
 */
export function calculateRateUnitWeightedAvgFromMergedPeriods(
  periods: Array<{
    peak?: number;
    offPeak?: number;
    shoulder?: number;
    averageDailyPeakEnergy?: number;
    averageDailyOffPeakEnergy?: number;
    averageDailyShoulderEnergy?: number;
    environmentalRatePeriods?: SegmentLike[];
  }>
): { peak: number; offPeak: number; shoulder: number } {
  let peakNum = 0,
    peakDen = 0,
    offNum = 0,
    offDen = 0,
    shNum = 0,
    shDen = 0;
  periods.forEach((p) => {
    const segs = p.environmentalRatePeriods ?? [];
    if (segs.length === 0) return;
    const dailyPeak = p.averageDailyPeakEnergy ?? 0;
    const dailyOff = p.averageDailyOffPeakEnergy ?? 0;
    const dailySh = p.averageDailyShoulderEnergy ?? 0;
    const {
      peak: weightedPeakRate,
      offPeak: weightedOffPeakRate,
      shoulder: weightedShoulderRate
    } = calculateWeightedRatesByEnergyFromSegments(segs, dailyPeak, dailyOff, dailySh);
    const periodPeakQty = sumBy(segs, (s) => s.peakConsumptionIncLoss ?? (s.numOfDays ?? 0) * dailyPeak);
    const periodOffQty = sumBy(segs, (s) => s.offPeakConsumptionIncLoss ?? (s.numOfDays ?? 0) * dailyOff);
    const periodShQty = sumBy(segs, (s) => s.shoulderConsumptionIncLoss ?? (s.numOfDays ?? 0) * dailySh);
    peakNum += weightedPeakRate * periodPeakQty;
    peakDen += periodPeakQty;
    offNum += weightedOffPeakRate * periodOffQty;
    offDen += periodOffQty;
    shNum += weightedShoulderRate * periodShQty;
    shDen += periodShQty;
  });
  return {
    peak: peakDen > 0 && peakNum > 0 ? peakNum / peakDen : 0,
    offPeak: offDen > 0 && offNum > 0 ? offNum / offDen : 0,
    shoulder: shDen > 0 && shNum > 0 ? shNum / shDen : 0
  };
}

/**
 * Aggregate cost fields from multiple periods
 */
function mergeMeterCostRecords(
  periods: CalculatedProjectOfferPeriod[],
  key: keyof CalculatedProjectOfferPeriod
): Record<string, number> {
  const out: Record<string, number> = {};
  periods.forEach((period) => {
    const record = (period as any)[key] as Record<string, number> | undefined;
    if (record) {
      Object.entries(record).forEach(([meterId, cost]) => {
        out[meterId] = (out[meterId] || 0) + cost;
      });
    }
  });
  return out;
}

export function aggregatePeriodCosts(periods: CalculatedProjectOfferPeriod[]): {
  periodContestableCosts: number;
  periodRenewalCosts: number;
  periodEnergyCosts: number;
  periodRegulatedCost: number;
  periodMeteringCharge: number;
  periodNetworkCost: number;
  periodTotalCosts: number;
  periodRetailerServiceFee: number;
  totalNumOfDays: number;
  meterContestableCosts: Record<string, number>;
  meterEnergyCosts: Record<string, number>;
  meterRenewableCosts: Record<string, number>;
  meterRetailerServiceFee: Record<string, number>;
  meterMeteringCharge: Record<string, number>;
} {
  return {
    periodContestableCosts: sumBy(periods, 'contestableCost'),
    periodRenewalCosts: sumBy(periods, 'renewableCost'),
    periodEnergyCosts: sumBy(periods, 'energyCost'),
    periodRegulatedCost: sumBy(periods, 'regulatedCost'),
    periodMeteringCharge: sumBy(periods, 'meteringCharge'),
    periodNetworkCost: sumBy(periods, 'networkCost'),
    periodTotalCosts: sumBy(periods, 'totalCosts'),
    periodRetailerServiceFee: sumBy(periods, 'retailerServiceFee'),
    totalNumOfDays: sumBy(periods, 'numOfDays'),
    meterContestableCosts: mergeMeterCostRecords(periods, 'meterContestableCosts'),
    meterEnergyCosts: mergeMeterCostRecords(periods, 'meterEnergyCosts'),
    meterRenewableCosts: mergeMeterCostRecords(periods, 'meterRenewableCosts'),
    meterRetailerServiceFee: mergeMeterCostRecords(periods, 'meterRetailerServiceFee'),
    meterMeteringCharge: mergeMeterCostRecords(periods, 'meterMeteringCharge')
  };
}
